import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type ReactNode,
  type ReactElement,
  cloneElement,
  isValidElement,
} from 'react';

import { createPortal } from 'react-dom';

import { cn } from '../../utils';

export interface TooltipProps {
  content: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  children: ReactElement | ReactNode;
  className?: string;
  /** Use portal to render tooltip outside of overflow containers */
  usePortal?: boolean;
}

const positionStyles = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

const arrowStyles = {
  top: 'top-full left-1/2 -translate-x-1/2',
  bottom: 'bottom-full left-1/2 -translate-x-1/2',
  left: 'left-full top-1/2 -translate-y-1/2',
  right: 'right-full top-1/2 -translate-y-1/2',
};

function getArrowBorderColor(position: TooltipProps['position']): React.CSSProperties {
  const surfaceColor = 'var(--tkf-tooltip-bg)';

  switch (position) {
    case 'top':
      return { borderTopColor: surfaceColor };
    case 'bottom':
      return { borderBottomColor: surfaceColor };
    case 'left':
      return { borderLeftColor: surfaceColor };
    case 'right':
      return { borderRightColor: surfaceColor };
    default:
      return { borderTopColor: surfaceColor };
  }
}

type Side = 'top' | 'bottom' | 'left' | 'right';

interface PortalPlacement {
  top: number;
  left: number;
  side: Side;
  /** Offset horizontal de la flecha dentro del tooltip (sólo top/bottom). */
  arrowLeft?: number;
}

const GAP = 8; // separación entre el trigger y el tooltip
const VIEWPORT_MARGIN = 8; // distancia mínima al borde de la pantalla

/**
 * Coloca el tooltip (portal, `position: fixed`) con sus medidas reales:
 * - top/bottom: si no cabe del lado pedido y sí del contrario, se voltea.
 * - Horizontalmente se centra sobre el trigger pero se recorta al viewport,
 *   y la flecha se desplaza para seguir apuntando al centro del trigger.
 * left/right conservan el cálculo centrado de siempre.
 */
function placePortalTooltip(trigger: DOMRect, tooltip: DOMRect, preferred: Side): PortalPlacement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (preferred === 'left' || preferred === 'right') {
    const top = trigger.top + trigger.height / 2 - tooltip.height / 2;
    const left = preferred === 'left' ? trigger.left - GAP - tooltip.width : trigger.right + GAP;
    return { top, left, side: preferred };
  }

  const fitsAbove = trigger.top - GAP - tooltip.height >= VIEWPORT_MARGIN;
  const fitsBelow = trigger.bottom + GAP + tooltip.height <= vh - VIEWPORT_MARGIN;
  let side: Side = preferred;
  if (preferred === 'top' && !fitsAbove && fitsBelow) side = 'bottom';
  if (preferred === 'bottom' && !fitsBelow && fitsAbove) side = 'top';

  const top = side === 'top' ? trigger.top - GAP - tooltip.height : trigger.bottom + GAP;
  const centerX = trigger.left + trigger.width / 2;
  const maxLeft = Math.max(VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - tooltip.width);
  const left = Math.min(Math.max(centerX - tooltip.width / 2, VIEWPORT_MARGIN), maxLeft);
  const arrowLeft = Math.min(Math.max(centerX - left, 8), tooltip.width - 8);

  return { top, left, side, arrowLeft };
}

function isDisabledTrigger(element: ReactElement): boolean {
  const props = element.props as Record<string, unknown>;
  const ariaDisabled = props['aria-disabled'];
  return Boolean(props.disabled) || ariaDisabled === true || ariaDisabled === 'true';
}

export function Tooltip({
  content,
  position = 'top',
  delay = 200,
  children,
  className,
  usePortal = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [placement, setPlacement] = useState<PortalPlacement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  const updatePlacement = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;
    setPlacement(
      placePortalTooltip(
        triggerRef.current.getBoundingClientRect(),
        tooltipRef.current.getBoundingClientRect(),
        position
      )
    );
  }, [position]);

  // Portal: se mide ya renderizado y se coloca antes del paint (layout
  // effect); se recalcula con scroll (de cualquier contenedor) y resize
  // mientras se ve.
  useLayoutEffect(() => {
    if (!isVisible || !usePortal) return;
    updatePlacement();
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);
    return () => {
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
    };
  }, [isVisible, usePortal, updatePlacement]);

  // Handle non-element children by wrapping in a span
  const childElement = isValidElement(children) ? children : <span>{children}</span>;
  const disabledTrigger = isDisabledTrigger(childElement);

  // Touch: un tap sobre un trigger DESHABILITADO muestra el motivo (es la única
  // forma de leerlo en móvil) y un tap fuera lo cierra. En triggers activos el
  // tap sigue sin mostrarlo: ahí el tap ejecuta la acción (ver nota abajo).
  useEffect(() => {
    if (!isVisible || !disabledTrigger) return;
    const handleOutside = (e: PointerEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) setIsVisible(false);
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [isVisible, disabledTrigger]);

  const side = usePortal && placement ? placement.side : position;

  const tooltipElement = (
    <div
      ref={tooltipRef}
      role="tooltip"
      className={cn(
        usePortal ? 'fixed' : 'absolute',
        'z-tooltip',
        'rounded border border-tkf-line-light px-2 py-1 text-xs font-medium',
        // `whitespace-normal` + `max-w-[280px]`: el tooltip wrappea en
        // múltiples líneas en lugar de salirse del viewport en textos
        // largos (>30 chars). Tooltips cortos (1-3 palabras) se ajustan
        // naturalmente al contenido y se ven idénticos al before porque
        // el max-w sólo entra en juego cuando el contenido excede.
        //
        // Override por call-site: pasar `className="whitespace-nowrap
        // max-w-none"` para volver al comportamiento estricto-una-línea
        // (e.g. badges "Online"/"Offline" donde wrappear se ve raro).
        'whitespace-normal max-w-[280px] animate-fade-in',
        // Portal: ancho según el contenido (hasta 280px, o el viewport en
        // móvil). Sin `w-max` el fixed se encoge y parte el texto por palabra.
        usePortal && 'w-max max-w-[min(280px,calc(100vw-16px))]',
        !usePortal && positionStyles[position],
        className
      )}
      style={
        usePortal
          ? {
              backgroundColor: 'var(--tkf-tooltip-bg)',
              color: 'var(--tkf-tooltip-fg)',
              top: placement?.top ?? 0,
              left: placement?.left ?? 0,
              // Primer render: invisible hasta medirlo y colocarlo.
              visibility: placement ? 'visible' : 'hidden',
            }
          : {
              backgroundColor: 'var(--tkf-tooltip-bg)',
              color: 'var(--tkf-tooltip-fg)',
            }
      }
    >
      {content}
      <span
        className={cn('absolute border-4 border-transparent', arrowStyles[side])}
        style={{
          ...getArrowBorderColor(side),
          ...(usePortal && placement?.arrowLeft !== undefined ? { left: placement.arrowLeft } : {}),
        }}
      />
    </div>
  );

  // Pointer events en el wrapper div para que disparen aun si el child es un
  // <button> deshabilitado. Solo mouse/pen lo muestran al pasar encima: en touch
  // un tap emula `pointerenter`, lo que lo dejaría "pegado" y empalmado con
  // cualquier modal que abra el mismo botón. En touch, el trigger deshabilitado
  // se abre con el tap y se cierra con un tap fuera; `pointerleave` llega justo
  // después de `pointerup` (spec de Pointer Events), así que no debe cerrarlo.
  // Focus/blur siguen en el child para accesibilidad por teclado.
  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onPointerEnter={e => {
        if (e.pointerType !== 'touch') showTooltip();
      }}
      onPointerDown={e => {
        if (e.pointerType === 'touch' && disabledTrigger) setIsVisible(v => !v);
      }}
      onPointerLeave={e => {
        if (e.pointerType !== 'touch') hideTooltip();
      }}
    >
      {cloneElement(childElement, {
        onFocus: (e: React.FocusEvent) => {
          showTooltip();
          childElement.props.onFocus?.(e);
        },
        onBlur: (e: React.FocusEvent) => {
          hideTooltip();
          childElement.props.onBlur?.(e);
        },
      })}

      {isVisible &&
        content &&
        (usePortal ? createPortal(tooltipElement, document.body) : tooltipElement)}
    </div>
  );
}
